
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Carousel, CarouselContent, CarouselItem } from "../../ui/carousel";
import slider_1 from "../../../assets/styles/slider_1.png"; 
import slider_2 from "../../../assets/styles/slider_2.png"; 
import slider_3 from "../../../assets/styles/silder_3.png"; 
import { Play } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import gsap from "gsap";
import useGsapContext from "../../../hooks/useGsapContext";
import usePrefersReducedMotion from "../../../hooks/usePrefersReducedMotion";
import useDebouncedCallback from "../../../hooks/useDebouncedCallback";
import useIntersectionVisible from "../../../hooks/useIntersectionVisible";

export default function ImageSlider() {
  // ---------------- Carousel state ----------------
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [needsReset, setNeedsReset] = useState(false);
  const autoPlayInterval = 5000;
  const location = useLocation();

  // ---------------- Refs ----------------
  const containerRef = useRef(null);
  const headingRef = useRef(null);
  const gradientTextRef = useRef(null);
  const descriptionRef = useRef(null);
  const buttonsRef = useRef(null);
  const sliderRef = useRef(null);

  // ---------------- GSAP refs (NEW – animation only) ----------------
  const tlRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  // ---------------- Intersection Observer ----------------
  const [sectionRef, isVisible] = useIntersectionVisible({
    ref: containerRef,
    threshold: 0.15,
    rootMargin: "0px 0px -10% 0px",
    triggerOnce: false
  });

  const combinedRef = useCallback(node => {
    containerRef.current = node;
    if (sectionRef) sectionRef.current = node;
  }, [sectionRef]);

  const prefersReducedMotion = usePrefersReducedMotion();

  const slides = [
    { centerImage: slider_1, alt: "Modern farming solutions" },
    { centerImage: slider_2, alt: "Agricultural products" },
    { centerImage: slider_3, alt: "Agastya's trusted solutions" }
  ];

  // ---------------- Carousel init ----------------
  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    const handleSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    api.on("select", handleSelect);
    return () => api.off("select", handleSelect);
  }, [api]);

  // ---------------- Page navigation lifecycle ----------------
  useEffect(() => {
    setIsComponentMounted(true);
    setAutoPlay(true);
    setNeedsReset(true);

    const timeoutId = setTimeout(() => {
      if (api) {
        api.scrollTo(1, { immediate: true });
        setAutoPlay(true);
      }
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      setIsComponentMounted(false);
      setAutoPlay(false);
    };
  }, [location]);

  // ---------------- Resize ----------------
  const handleResize = useDebouncedCallback(() => {
    if (typeof window === "undefined") return;
    setIsMobile(window.innerWidth < 768);
  }, 150);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // ---------------- Autoplay ----------------
  const pauseAutoPlay = useCallback(() => setAutoPlay(false), []);
  const resumeAutoPlay = useCallback(() => setAutoPlay(true), []);

  useEffect(() => {
    if (!api || !autoPlay || !isVisible || !isComponentMounted) return;

    const interval = setInterval(() => {
      if (api && count > 0) {
        api.scrollTo((api.selectedScrollSnap() + 1) % count);
      }
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [api, autoPlay, count, isVisible, isComponentMounted]);

  // ======================================================
  // ✅ GSAP HERO ANIMATION (COMBINED & FIXED)
  // ======================================================
  useEffect(() => {
    // 🔒 Run ONLY once
    if (
      hasAnimatedRef.current ||
      !isVisible ||
      prefersReducedMotion ||
      !isComponentMounted
    ) {
      return;
    }

    hasAnimatedRef.current = true;

    const ctx = gsap.context(() => {
      const elements = [
        { ref: headingRef, y: 30, duration: 0.8 },
        { ref: gradientTextRef, y: 20, duration: 0.7 },
        { ref: descriptionRef, y: 15, duration: 0.8 },
        { ref: buttonsRef, y: 20, duration: 0.6 },
        { ref: sliderRef, y: 40, duration: 0.8 }
      ];

      // Initial state (ONLY ONCE)
      elements.forEach(({ ref, y }) => {
        if (ref.current) {
          gsap.set(ref.current, {
            opacity: 0,
            y,
            force3D: true
          });
        }
      });

      const tl = gsap.timeline({
        defaults: {
          ease: "power2.out",
          clearProps: "transform"
        }
      });

      tlRef.current = tl;

      elements.forEach(({ ref, duration }, index) => {
        if (ref.current) {
          tl.to(
            ref.current,
            { opacity: 1, y: 0, duration },
            index === 0 ? 0 : "-=0.35"
          );
        }
      });
    }, containerRef);

    return () => {
      if (tlRef.current) {
        tlRef.current.kill();
        tlRef.current = null;
      }
      ctx.revert();
    };
  }, [isVisible, prefersReducedMotion, isComponentMounted]);

  return (
    <div 
      ref={combinedRef}
      className="flex flex-col items-center justify-center gap-[52px] relative md:py-[80px] py-[60px] px-0 -mt-[20px] z-10"
      aria-label="Hero section with image slider"
    >
      <div className="w-full">
        {/* Heading Section */}
        <div className="flex flex-col w-full max-w-[650px] mx-auto items-center justify-center gap-6 px-[25px] md:px-0">
          <div className="text-center">
            <h1 
              ref={headingRef} 
              className="text-[34px] md:text-[65px] font-bold text-[#222222] font-[LTWave] leading-[113%] tracking-[1%]"
            >
              Quality Solutions For{" "}
              <span 
                ref={gradientTextRef} 
                className="bg-[linear-gradient(90deg,#593747_0%,#DB4B12_59%,#FFB603_100%)] text-transparent bg-clip-text inline-block"
              >
                Modern Farming
              </span>
            </h1>
          </div>
          
          <div className="text-center">
            <p 
              ref={descriptionRef} 
              className="w-full font-regular inter-font text-[#6c6c6c] text-sm md:text-lg text-center leading-[141%] tracking-[0%] px-2"
            >
              Unlock the potential of your crops with our best agricultural products.
              Experience growth like never before with Agastya's trusted solutions.
            </p>
          </div>
          
          <div ref={buttonsRef} className="flex items-center gap-4">
            {/* Contact Us Button */}
            <Link 
              to="/contact"
              aria-label="Contact us"
            >
              <div className="w-[182px] h-12 rounded-full overflow-hidden 
                bg-[linear-gradient(90deg,#593747_0%,#DB4B12_59%,#FFB603_100%)]
                flex items-center justify-center cursor-pointer
                transition-all duration-300 ease-in-out
                hover:scale-105 hover:shadow-lg">
                <span className="font-bold text-white font-[LTWave] text-lg tracking-[0.18px]">
                  Contact us
                </span>
              </div>
            </Link>

            {/* Play Button */}
            <Link 
              to="/about-us"
              aria-label="Learn more about us"
            >
              <div 
                className="w-12 h-12 rounded-full overflow-hidden 
                bg-gradient-to-b from-[#fdb203] via-[#cd4917] to-[#6f3a3e] 
                flex items-center justify-center cursor-pointer
                transition-all duration-300 ease-in-out
                hover:scale-110 hover:shadow-lg hover:rotate-6">
                <Play className="h-4 w-4 fill-white text-white" />
              </div>
            </Link>
          </div>
        </div>

        {/* Slider Section */}
        <div ref={sliderRef} className="w-full relative mt-[53px]">
          <Carousel 
            setApi={setApi} 
            className="w-full"
            opts={{
              align: "center",
              containScroll: "trimSnaps",
              startIndex: 1,
              loop: true,
              dragFree: false
            }}
          >
            <CarouselContent className="-ml-4 md:-ml-8">
              {slides.map((slide, index) => (
                <CarouselItem 
                  key={index} 
                  className="pl-4 md:pl-8 xl:basis-[85%] md:basis-[75%]"
                >
                  <div className="overflow-hidden rounded-[30px] relative w-full max-w-[238px] md:max-w-full aspect-[3/4] md:h-[400px] lg:h-[476px] shadow-lg">
                    <div className="flex h-full"> 
                      <div className="flex-1 h-full relative">
                        <div className="absolute inset-0 z-10"></div>
                        
                        <img 
                          src={slide.centerImage} 
                          alt={slide.alt} 
                          className="w-full h-full object-cover"
                          loading={index === 0 ? "eager" : "lazy"}
                        />
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          
          {/* Navigation dots - Uncomment if needed */}
          {/* <div className="flex justify-center gap-3 mt-6">
            {count > 0 && Array.from({ length: count }, (_, index) => (
              <button
                key={index}
                className={`w-3 h-3 rounded-full shadow-md transition-all hover:scale-110 ${
                  index === current - 1 
                    ? "bg-[#FFB603]" 
                    : "bg-white/60 hover:bg-white"
                }`}
                onClick={() => scrollTo(index)}
                aria-label={`View slide ${index + 1}`}
              />
            ))}
          </div> */}
        </div>
      </div>    
    </div>
  );
} 